import { useMutation } from '@tanstack/react-query';
import { Button, Checkbox, Typography } from 'antd';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './MondayInstall.less';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { isAxiosError } from 'axios';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { AUTH0_DOMAIN } from '@config/constants';
import { apiClient } from '@api/client';
import { ErrorPage404, ErrorPage500 } from '@pages/ErrorPage';

const { Title, Paragraph } = Typography;

export default function MondayOAuthInstall() {
  const [searchParams] = useSearchParams();
  const stateParam = searchParams.get('state');
  const tokenParam = searchParams.get('authorization_bearer');
  const mutation = useMutation({
    mutationFn: async () => {
      return apiClient.monday.mondayControllerInstall({
        headers: { Authorization: `Bearer ${tokenParam}` }
      });
    },
    onSuccess: () => {
      const qs = new URLSearchParams(searchParams);
      qs.delete('authorization_bearer');
      window.location.href = `https://${AUTH0_DOMAIN}/continue?${qs.toString()}`;
    }
  });
  const { mutate } = mutation;
  const [consentGiven, setConsentGiven] = useState(false);

  if (!stateParam || !tokenParam) {
    return <ErrorPage404 onGoBack={() => window.close()} />;
  }

  if (mutation.isError) {
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode === 'feature_not_allowed'
    ) {
      const feature = mutation.error.response?.data.feature;
      const plan =
        feature === 'advanced_integrations' ? 'an Enterprise' : 'a Team';
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <Title level={2} className="monday-install__title">
              <UpgradePlanSvg style={{ width: 26, height: 26 }} /> Upgrade to{' '}
              {plan} plan to install the monday.com integration
            </Title>
          </div>
        </div>
      );
    }
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.status === 403
    ) {
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <div className="monday-install__lock-icon">
              <LockSvg />
            </div>
            <Title level={2} className="monday-install__title">
              You don&apos;t have permissions to install integrations
            </Title>
            <Title level={5} className="monday-install__title">
              Contact your organization admin to install the monday.com
              integration
            </Title>
          </div>
        </div>
      );
    }
    return <ErrorPage500 onGoBack={() => window.close()} />;
  }

  return (
    <div className="monday-install">
      <div className="monday-install__content">
        <Title level={2} className="monday-install__title">
          Data Retention Consent
        </Title>
        <Paragraph className="monday-install__description">
          StreamWork is a standalone platform, and the monday.com app is just
          one way to stay connected. To ensure you don&apos;t lose access to
          your projects and settings if the monday.com app is ever removed, we
          need your permission to keep your account data active.
        </Paragraph>
        <Paragraph className="monday-install__description">
          By checking this box, you agree to let StreamWork retain your data so
          you can keep using your account independently at any time. You&apos;re
          always in control — you can delete your data whenever you like by
          closing your StreamWork account or by reaching out to{' '}
          <a href="mailto:support@streamwork.com">support@streamwork.com</a>.
        </Paragraph>
        <Checkbox
          className="monday-install__description"
          value={consentGiven}
          onChange={(e) => setConsentGiven(e.target.checked)}
        >
          <b>
            I agree to StreamWork retaining my data for continued platform use
          </b>
        </Checkbox>
        <Button
          className="monday-install__button"
          type="primary"
          onClick={() => mutate()}
          disabled={!consentGiven}
          loading={mutation.isPending || mutation.isSuccess}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
